import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { SkillShort, SkillsDetectionRequest } from '~/shared/data-contract/skill';
import { useLearningOpportunitiesCustomAttributes } from '~/shared/query-hooks/learning-opportunities';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { SidepanelTitle } from '~/ui/domain/Sidepanel/components/SidepanelTitle';

import { useSkillsDetectionQuery } from '../../app/query-hooks/skills';
import {
    ContentContainer,
    ContentSections,
    FlexRowContainer,
    StyledStickyHeader,
    useDiscardConfirmation,
    Wrapper,
} from '../../job-architecture-v2';
import { useFeaturedOpportunityOptionsQuery, useLearningOpportunityPermissions } from '../api';
import type { LearningOpportunity, LearningOpportunityCreate } from '../data-contract';
import { getLocalizedTranslationIndex, type LearningOpportunityFormState, submitFormPayload } from '../form-state';
import { type EditableLearningOpportunityState, learningLanguageCodes } from '../helpers';
import { useAutoAddDetectedSkills } from '../hooks/use-auto-add-detected-skills';
import { CourseActionBar, CourseActionBarContent } from './CourseActionBar';
import { CourseMetadataRow } from './CourseMetadataRow';
import { CourseTypeRow } from './CourseTypeRow';
import { DescriptionSection } from './DescriptionSection';
import { LearningOpportunityActions } from './LearningOpportunityActions';
import { LearningOpportunityPublishNotification } from './LearningOpportunityPublishNotification';
import { SidepanelHeaderNavigation, SidepanelHeaderTitle } from './SidepanelHeader';
import { SkillsColumn } from './SkillsColumn';
import { UrlSection } from './UrlSection';

export const LearningOpportunityContent: FunctionComponent<{
    readonly defaultValue: LearningOpportunityFormState;
    readonly isPublishing?: boolean;
    readonly isSubmitting?: boolean;
    readonly learningOpportunity?: LearningOpportunity;
    readonly mode: 'create' | 'edit' | 'view';
    readonly onCancel: () => void;
    readonly onClose: () => void;
    readonly onEdit?: () => void;
    readonly onPublish?: () => void;
    readonly onSkillClick?: (skillId: string, preferredLanguage?: string) => void;
    readonly onTrainedSkillsChange?: (skills: readonly SkillShort[]) => void;
    readonly onSubmit: (value: LearningOpportunityCreate) => void;
}> = ({
    defaultValue,
    isPublishing = false,
    isSubmitting = false,
    learningOpportunity,
    mode,
    onCancel,
    onClose,
    onEdit,
    onPublish,
    onSkillClick,
    onSubmit,
    onTrainedSkillsChange,
}) => {
    const isEditing = mode === 'create' || mode === 'edit';
    const translate = useTranslate();
    const {
        applicationState: { availableLanguages, customization, language },
    } = useApplicationContext();
    const featuredOptions = useFeaturedOpportunityOptionsQuery(learningOpportunity?.id ?? '');
    const { canUserEditLearningOpportunity, isPending: isPermissionsPending } = useLearningOpportunityPermissions(
        learningOpportunity?.id ?? '',
    );
    const customAttributes = useLearningOpportunitiesCustomAttributes();
    const providerOptions = customization
        .get('learningOpportunitiesConfiguration')
        .get('learningOpportunityProviders')
        .unwrapOr([] as string[]);
    const skillLimit = customization
        .get('learningOpportunitiesConfiguration')
        .get('learningOpportunitySkillCountLimit')
        .unwrapOr(50);
    const languageCodes = Object.values(availableLanguages).map((item) => item.code);
    const [activeLanguage, setActiveLanguage] = useState(
        languageCodes.includes(language.code)
            ? language.code
            : (defaultValue.translations[0]?.language ?? language.code),
    );
    const [blacklistedDetectedSkillIds, setBlacklistedDetectedSkillIds] = useState<readonly string[]>([]);
    const methods = useForm<LearningOpportunityFormState>({
        defaultValues: defaultValue,
        mode: 'onChange',
    });
    const values = methods.watch();
    const trainedSkills = methods.watch('skills') ?? [];
    const translationIndex = getLocalizedTranslationIndex(values, activeLanguage);
    const activeTranslation = values.translations[translationIndex];
    const detectionPayload = useMemo<SkillsDetectionRequest>(
        () => ({
            language: activeLanguage,
            text: activeTranslation?.description ?? '',
            title: activeTranslation?.title ?? '',
        }),
        [activeLanguage, activeTranslation?.description, activeTranslation?.title],
    );
    const skillsDetectionQuery = useSkillsDetectionQuery('course', detectionPayload, mode === 'create', {
        includeProficienciesSearchParam: false,
    });
    useAutoAddDetectedSkills({
        blacklistedSkillIds: blacklistedDetectedSkillIds,
        detectedSkills: skillsDetectionQuery.data ?? [],
        methods,
        mode,
        skillLimit,
    });

    useEffect(() => {
        onTrainedSkillsChange?.(trainedSkills);
    }, [onTrainedSkillsChange, trainedSkills]);

    const handleSkillRemoved = (skillId: string): void => {
        if (mode !== 'create') return;
        setBlacklistedDetectedSkillIds((current) => (current.includes(skillId) ? current : [...current, skillId]));
    };
    const handleStateChange = (next: EditableLearningOpportunityState): void => {
        methods.setValue('state', next, { shouldDirty: true, shouldValidate: true });
    };
    const submit = (value: LearningOpportunityFormState): void => submitFormPayload(value, onSubmit);

    const {
        handleCancel: handleDiscardCancel,
        handleCancelDiscard,
        handleClose: handleDiscardClose,
        handleConfirmDiscard,
        isDiscardDialogOpen,
    } = useDiscardConfirmation({
        formMethods: methods,
        isEditing,
        onClose,
        setIsEditing: (editing) => {
            if (!editing) onCancel();
        },
    });

    return (
        <FormProvider {...methods}>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
            />
            <form onSubmit={methods.handleSubmit(submit)} className="w-full">
                <Wrapper>
                    <StyledStickyHeader>
                        <SidepanelHeaderNavigation
                            activeLanguage={activeLanguage}
                            actions={
                                learningOpportunity === undefined ? undefined : (
                                    <LearningOpportunityActions
                                        canManageFeaturedLearningOpportunities={
                                            featuredOptions.data?.canManageFeaturedLearningOpportunities === true
                                        }
                                        learningOpportunity={learningOpportunity}
                                    />
                                )
                            }
                            availableLanguages={languageCodes}
                            onClose={handleDiscardClose}
                            onLanguageChange={setActiveLanguage}
                        />
                    </StyledStickyHeader>
                    {learningOpportunity !== undefined &&
                        learningOpportunity.state === 'draft' &&
                        canUserEditLearningOpportunity &&
                        onPublish !== undefined && (
                            <LearningOpportunityPublishNotification
                                disabled={isPublishing || isEditing}
                                onPublish={onPublish}
                            />
                        )}
                    <SidepanelHeaderTitle>
                        <CourseTypeRow />
                        <SidepanelTitle
                            key={translationIndex}
                            isEditing={isEditing}
                            placeholder={translate('pages.learning-opportunities.title-placeholder')}
                            register={methods.register(`translations.${translationIndex}.title`, {
                                maxLength: 128,
                                minLength: 3,
                                required: true,
                            })}
                            title={values.translations[translationIndex]?.title ?? learningOpportunity?.title ?? ''}
                        />
                    </SidepanelHeaderTitle>
                    <ContentContainer>
                        {learningOpportunity !== undefined && (
                            <CourseMetadataRow
                                isEditing={isEditing}
                                learningLanguage={learningOpportunity.learningLanguage}
                                onStateChange={handleStateChange}
                                ownerName={learningOpportunity.ownerName}
                                provider={learningOpportunity.provider}
                                state={isEditing ? values.state : learningOpportunity.state}
                                updatedAt={learningOpportunity.updatedAt}
                            />
                        )}
                        <CourseActionBar>
                            <CourseActionBarContent
                                canUserEditLearningOpportunity={canUserEditLearningOpportunity}
                                isEditing={isEditing}
                                isPermissionsPending={isPermissionsPending}
                                isSubmitting={isSubmitting}
                                learningOpportunity={learningOpportunity}
                                onCancel={handleDiscardCancel}
                                onEdit={onEdit}
                            />
                        </CourseActionBar>
                        <FlexRowContainer>
                            <ContentSections data-testid="learning-opportunity-content">
                                <div className="flex flex-col gap-xl">
                                    <DescriptionSection
                                        fallbackHtml={learningOpportunity?.description}
                                        isEditing={isEditing}
                                        translationIndex={translationIndex}
                                    />
                                    <section
                                        className="flex flex-col gap-m"
                                        data-testid="learning-opportunity-url-section"
                                    >
                                        <UrlSection
                                            customAttributes={customAttributes}
                                            isEditing={isEditing}
                                            learningLanguageCodes={learningLanguageCodes}
                                            providerOptions={providerOptions}
                                            url={values.url}
                                        />
                                    </section>
                                </div>
                                <div className="flex flex-col gap-xl">
                                    <SkillsColumn
                                        isDetectionPending={mode === 'create' && skillsDetectionQuery.isFetching}
                                        isEditing={isEditing}
                                        languageCode={activeLanguage}
                                        skills={values.skills ?? []}
                                        onSkillClick={onSkillClick}
                                        onSkillRemoved={handleSkillRemoved}
                                    />
                                </div>
                            </ContentSections>
                        </FlexRowContainer>
                    </ContentContainer>
                </Wrapper>
            </form>
        </FormProvider>
    );
};
